// frontend/src/pages/admin/Licenses.js
//
// Issuing GlamGo to other shops, and seeing what is running out there.
//
// Two things on this page are irreversible and both are treated as such:
//
//   · the KEY is shown exactly once. Only its SHA-256 is stored, so an operator
//     who closes the dialog without copying it has to issue a new licence. The
//     dialog therefore does not close on a click outside, has no X until the key
//     has been copied, and says plainly what is about to be lost.
//   · REVOKING cannot be undone and stops a real shop taking bookings, so it
//     asks for a reason in writing rather than a yes/no.

import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { errorText } from '../../utils/errors';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getIntlLocale } from '../../i18n';
import {
  Plus, X, Save, Copy, Check, Ban, Eye, PauseCircle, PlayCircle, Unplug, KeyRound,
} from 'lucide-react';
import theme from '../../theme';

const FONTS = '/fonts/fonts.css';

const EMPTY_FORM = {
  licensee_name: '', licensee_email: '', licensee_country: 'CY', licensee_vat: '',
  plan: 'starter', max_lockers: '1', max_products: '', max_activations: '1',
  expires_at: '', grace_days: '14', affiliate_code: '', sale_value: '', notes: '',
};

export default function AdminLicenses() {
  const { t } = useTranslation();
  const dateLocale = getIntlLocale();

  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);   // the once-only key dialog
  const [copied, setCopied] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    load();
    return () => document.head.removeChild(link);
  }, []);

  const load = () => {
    setLoading(true);
    api.get('/licenses')
      .then(setLicenses)
      .catch(() => toast.error(t('admin.loadError')))
      .finally(() => setLoading(false));
  };

  const issue = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await api.post('/licenses', {
        ...form,
        max_lockers: parseInt(form.max_lockers, 10) || 1,
        // An empty field means "unlimited" for products and "not set" for the
        // rest; undefined is how the route is told to apply its own default,
        // and '' would be sent as a value.
        max_products: form.max_products ? parseInt(form.max_products, 10) : null,
        max_activations: parseInt(form.max_activations, 10) || 1,
        grace_days: parseInt(form.grace_days, 10),
        expires_at: form.expires_at || undefined,
        affiliate_code: form.affiliate_code || undefined,
        sale_value: form.sale_value ? parseFloat(form.sale_value) : undefined,
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      setIssued(created);
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.licenses.issueFailed')));
    } finally {
      setSaving(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(issued.key);
      setCopied(true);
    } catch {
      toast(t('admin.licenses.copyManually'));
      // Marked copied anyway: the key is on screen, and refusing to let the
      // operator dismiss the dialog because a clipboard API is unavailable
      // traps them with the one thing they must not lose.
      setCopied(true);
    }
  };

  const suspend = async (l) => {
    const reason = window.prompt(t('admin.licenses.suspendPrompt', { name: l.licensee_name }));
    if (!reason) return;
    try {
      await api.patch(`/licenses/${l.id}`, { status: 'suspended', suspended_reason: reason });
      toast.success(t('admin.licenses.suspended'), { duration: 6000 });
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.licenses.updateFailed')));
    }
  };

  const reinstate = async (l) => {
    try {
      await api.patch(`/licenses/${l.id}`, { status: 'active', suspended_reason: null });
      toast.success(t('admin.licenses.reinstated'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.licenses.updateFailed')));
    }
  };

  const revoke = async (l) => {
    const reason = window.prompt(t('admin.licenses.revokePrompt', { name: l.licensee_name }));
    if (!reason) return;
    try {
      const result = await api.post(`/licenses/${l.id}/revoke`, { reason });
      toast.success(t('admin.licenses.revoked', { count: result.commissions_cancelled || 0 }), { duration: 7000 });
      setDetail(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.licenses.updateFailed')));
    }
  };

  const releaseActivation = async (licenseId, activation) => {
    if (!window.confirm(t('admin.licenses.releaseConfirm', { host: activation.hostname || activation.instance_id }))) return;
    try {
      await api.delete(`/licenses/${licenseId}/activations/${activation.id}`);
      toast.success(t('admin.licenses.released'));
      setDetail(await api.get(`/licenses/${licenseId}`));
    } catch (err) {
      toast.error(errorText(err, t('admin.licenses.updateFailed')));
    }
  };

  const open = async (l) => {
    try { setDetail(await api.get(`/licenses/${l.id}`)); }
    catch (err) { toast.error(errorText(err, t('admin.loadError'))); }
  };

  const statusColour = (status) => ({
    active:    { bg: theme.greenWash, fg: theme.greenDeep },
    issued:    { bg: theme.creamDeep, fg: theme.grey550 },
    suspended: { bg: theme.amberWash, fg: theme.amber },
    expired:   { bg: theme.grey100, fg: theme.grey450 },
    revoked:   { bg: theme.grey100, fg: theme.red },
  }[status] || { bg: theme.grey100, fg: theme.grey450 });

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>
              {t('admin.licenses.title')}
            </h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>
              {t('admin.licenses.count', { count: licenses.length })}
            </p>
          </div>
          <button onClick={() => setShowForm(true)} style={primaryBtn}>
            <Plus size={16} /> {t('admin.licenses.newLicense')}
          </button>
        </div>

        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 900 }}>
            <thead>
              <tr style={{ background: theme.cream }}>
                {['colLicensee', 'colKey', 'colPlan', 'colSeats', 'colExpiry', 'colLastSeen', 'colStatus', 'colActions']
                  .map((h) => <th key={h} style={th}>{t(`admin.licenses.${h}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: theme.gold }}>{t('admin.licenses.loading')}</td></tr>
              ) : licenses.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.licenses.none')}</td></tr>
              ) : licenses.map((l) => {
                const colour = statusColour(l.status);
                const expiringSoon = l.expires_at &&
                  new Date(l.expires_at) - Date.now() < 30 * 86400000 &&
                  ['issued', 'active'].includes(l.status);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${theme.creamDeep}`, opacity: l.status === 'revoked' ? 0.5 : 1 }}>
                    <td style={{ padding: '15px 16px' }}>
                      <div style={{ color: theme.ink }}>{l.licensee_name}</div>
                      <div style={{ fontSize: 12, color: theme.grey400 }}>{l.licensee_email}</div>
                      {l.affiliate_code && (
                        <div style={{ fontSize: 11, color: theme.gold, marginTop: 2 }}>
                          {t('admin.licenses.soldBy', { partner: l.affiliate_name })}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '15px 16px' }}>
                      <span style={{ fontFamily: theme.font.mono, fontSize: 12.5, color: theme.grey600 }}>{l.key_prefix}…</span>
                    </td>
                    <td style={td}>{t(`admin.licenses.plan.${l.plan}`)}</td>
                    <td style={td}>{l.activation_count}/{l.max_activations}</td>
                    <td style={{ ...td, color: expiringSoon ? theme.amber : theme.grey600 }}>
                      {l.expires_at ? new Date(l.expires_at).toLocaleDateString(dateLocale) : t('admin.licenses.perpetual')}
                    </td>
                    <td style={td}>
                      {l.last_seen_at ? new Date(l.last_seen_at).toLocaleDateString(dateLocale) : t('admin.licenses.neverSeen')}
                    </td>
                    <td style={{ padding: '15px 16px' }}>
                      <span style={{
                        background: colour.bg, color: colour.fg, padding: '3px 10px',
                        borderRadius: 100, fontSize: 11, fontWeight: 500,
                      }}>{t(`admin.licenses.status.${l.status}`)}</span>
                    </td>
                    <td style={{ padding: '15px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => open(l)} title={t('admin.licenses.viewTitle')} style={iconBtn}><Eye size={14} /></button>
                        {l.status === 'active' || l.status === 'issued' ? (
                          <button onClick={() => suspend(l)} title={t('admin.licenses.suspendTitle')} style={{ ...iconBtn, color: theme.amber }}>
                            <PauseCircle size={15} />
                          </button>
                        ) : l.status === 'suspended' ? (
                          <button onClick={() => reinstate(l)} title={t('admin.licenses.reinstateTitle')} style={{ ...iconBtn, color: theme.green }}>
                            <PlayCircle size={15} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Issue ── */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setForm(EMPTY_FORM); }} title={t('admin.licenses.modalTitle')}>
          <form onSubmit={issue}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label={t('admin.licenses.labelLicensee')} span={2}>
                <input value={form.licensee_name} onChange={(e) => setForm({ ...form, licensee_name: e.target.value })} required style={input} />
              </Field>
              <Field label={t('admin.licenses.labelEmail')} span={2} hint={t('admin.licenses.hintEmail')}>
                <input type="email" value={form.licensee_email} onChange={(e) => setForm({ ...form, licensee_email: e.target.value })} required style={input} />
              </Field>
              <Field label={t('admin.licenses.labelCountry')}>
                <input maxLength={2} value={form.licensee_country}
                       onChange={(e) => setForm({ ...form, licensee_country: e.target.value.toUpperCase() })} style={input} />
              </Field>
              <Field label={t('admin.licenses.labelVat')}>
                <input value={form.licensee_vat} onChange={(e) => setForm({ ...form, licensee_vat: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.licenses.labelPlan')}>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} style={{ ...input, cursor: 'pointer' }}>
                  {['trial', 'starter', 'pro', 'enterprise'].map((p) => (
                    <option key={p} value={p}>{t(`admin.licenses.plan.${p}`)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('admin.licenses.labelExpiry')} hint={t('admin.licenses.hintExpiry')}>
                <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.licenses.labelMaxLockers')}>
                <input type="number" min="1" value={form.max_lockers} onChange={(e) => setForm({ ...form, max_lockers: e.target.value })} style={input} />
              </Field>
              <Field label={t('admin.licenses.labelMaxProducts')} hint={t('admin.licenses.hintUnlimited')}>
                <input type="number" min="1" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: e.target.value })} placeholder="∞" style={input} />
              </Field>

              <Field label={t('admin.licenses.labelMaxActivations')} hint={t('admin.licenses.hintActivations')}>
                <input type="number" min="1" value={form.max_activations} onChange={(e) => setForm({ ...form, max_activations: e.target.value })} style={input} />
              </Field>
              <Field label={t('admin.licenses.labelGrace')} hint={t('admin.licenses.hintGrace')}>
                <input type="number" min="0" max="365" value={form.grace_days} onChange={(e) => setForm({ ...form, grace_days: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.licenses.labelAffiliate')} hint={t('admin.licenses.hintAffiliate')}>
                <input value={form.affiliate_code}
                       onChange={(e) => setForm({ ...form, affiliate_code: e.target.value.toUpperCase() })}
                       style={{ ...input, fontFamily: theme.font.mono }} />
              </Field>
              <Field label={t('admin.licenses.labelSaleValue')} hint={t('admin.licenses.hintSaleValue')}>
                <input type="number" min="0" step="0.01" value={form.sale_value}
                       onChange={(e) => setForm({ ...form, sale_value: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.licenses.labelNotes')} span={2}>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} style={{ ...input, resize: 'vertical' }} />
              </Field>
            </div>

            <button type="submit" disabled={saving} style={{ ...primaryBtn, width: '100%', justifyContent: 'center', marginTop: 20, padding: 16 }}>
              <Save size={16} /> {saving ? t('admin.licenses.issuing') : t('admin.licenses.issue')}
            </button>
          </form>
        </Modal>
      )}

      {/* ── The key, shown once ── */}
      {issued && (
        <div style={{
          position: 'fixed', inset: 0, background: theme.alpha.black(0.66), zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 560, padding: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <KeyRound size={20} color={theme.gold} />
              <h2 style={{ fontFamily: theme.font.display, fontSize: 26, margin: 0, fontWeight: 400 }}>
                {t('admin.licenses.keyTitle')}
              </h2>
            </div>
            <p style={{ color: theme.grey550, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
              {t('admin.licenses.keyWarning', { name: issued.licensee_name })}
            </p>

            <div style={{
              background: theme.creamDeep, borderRadius: 4, padding: '18px 20px',
              fontFamily: theme.font.mono, fontSize: 17, letterSpacing: '0.08em',
              color: theme.ink, textAlign: 'center', overflowWrap: 'anywhere', marginBottom: 18,
            }}>{issued.key}</div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={copyKey} style={{ ...primaryBtn, flex: 1, justifyContent: 'center', background: copied ? theme.green : theme.ink }}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? t('admin.licenses.keyCopied') : t('admin.licenses.copyKey')}
              </button>
              {/* Dismissal is gated on having copied, because there is no second
                  chance and no "resend" — only the hash is stored. */}
              <button
                onClick={() => { setIssued(null); setCopied(false); }}
                disabled={!copied}
                style={{
                  ...primaryBtn, background: 'none', color: copied ? theme.grey600 : theme.grey400,
                  border: `1px solid ${theme.sand}`, cursor: copied ? 'pointer' : 'not-allowed',
                }}>
                {t('admin.licenses.done')}
              </button>
            </div>
            <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, marginTop: 16, marginBottom: 0 }}>
              {t('admin.licenses.keyEmailed')}
            </p>
          </div>
        </div>
      )}

      {/* ── Detail ── */}
      {detail && (
        <Modal onClose={() => setDetail(null)} title={detail.licensee_name} wide>
          <div style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.9, marginBottom: 24 }}>
            <div><strong>{t('admin.licenses.colKey')}:</strong> <span style={{ fontFamily: theme.font.mono }}>{detail.key_prefix}…</span></div>
            <div><strong>{t('admin.licenses.colPlan')}:</strong> {t(`admin.licenses.plan.${detail.plan}`)}</div>
            <div><strong>{t('admin.licenses.colStatus')}:</strong> {t(`admin.licenses.status.${detail.status}`)}</div>
            <div><strong>{t('admin.licenses.colExpiry')}:</strong> {detail.expires_at ? new Date(detail.expires_at).toLocaleDateString(dateLocale) : t('admin.licenses.perpetual')}</div>
            <div><strong>{t('admin.licenses.labelGrace')}:</strong> {t('admin.licenses.graceDays', { count: detail.grace_days })}</div>
            {detail.suspended_reason && <div style={{ color: theme.amber }}><strong>{t('admin.licenses.suspendedReason')}:</strong> {detail.suspended_reason}</div>}
            {detail.revoked_reason && <div style={{ color: theme.red }}><strong>{t('admin.licenses.revokedReason')}:</strong> {detail.revoked_reason}</div>}
          </div>

          <h3 style={h3}>{t('admin.licenses.activationsTitle')}</h3>
          <div style={{ border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 460 }}>
              <thead>
                <tr style={{ background: theme.cream }}>
                  {['colHost', 'colVersion', 'colFirstSeen', 'colLastSeenShort', 'colActions']
                    .map((h) => <th key={h} style={{ ...th, fontSize: 11 }}>{t(`admin.licenses.${h}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {detail.activations.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 28, textAlign: 'center', color: theme.grey400 }}>
                    {t('admin.licenses.noActivations')}
                  </td></tr>
                ) : detail.activations.map((a) => (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                    <td style={{ ...td, fontSize: 13 }}>
                      {a.hostname || '—'}
                      <div style={{ fontSize: 11, color: theme.grey400, fontFamily: theme.font.mono }}>{a.instance_id.slice(0, 12)}…</div>
                    </td>
                    <td style={{ ...td, fontSize: 13 }}>{a.app_version || '—'}</td>
                    <td style={{ ...td, fontSize: 13 }}>{new Date(a.first_seen_at).toLocaleDateString(dateLocale)}</td>
                    <td style={{ ...td, fontSize: 13 }}>{new Date(a.last_seen_at).toLocaleDateString(dateLocale)}</td>
                    <td style={{ padding: '15px 16px' }}>
                      <button onClick={() => releaseActivation(detail.id, a)}
                              title={t('admin.licenses.releaseTitle')}
                              style={{ ...iconBtn, color: theme.grey600 }}>
                        <Unplug size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: '12px 0 0' }}>
            {t('admin.licenses.releaseNote')}
          </p>

          {detail.status !== 'revoked' && (
            <>
              <h3 style={h3}>{t('admin.licenses.dangerTitle')}</h3>
              <button onClick={() => revoke(detail)} style={{ ...primaryBtn, background: theme.red }}>
                <Ban size={16} /> {t('admin.licenses.revoke')}
              </button>
              <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, marginTop: 10 }}>
                {t('admin.licenses.revokeNote')}
              </p>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Presentation ───────────────────────────────────────────────────────────

const th = { padding: '14px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em' };
const td = { padding: '15px 16px', color: theme.grey600, fontSize: 13 };
const h3 = { fontFamily: theme.font.display, fontSize: 19, fontWeight: 400, color: theme.ink, margin: '28px 0 12px' };
const input = {
  width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4,
  fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white,
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 8, background: theme.ink, color: theme.white,
  border: 'none', padding: '14px 24px', borderRadius: 2, cursor: 'pointer',
  fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body,
};
const iconBtn = {
  background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4,
  padding: '6px 10px', cursor: 'pointer', color: theme.grey600, display: 'flex', alignItems: 'center',
};

function Field({ label, hint, span = 1, children }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{label}</label>
      {children}
      {hint && <p style={{ margin: '6px 0 0', fontSize: 11, color: theme.grey400, lineHeight: 1.6 }}>{hint}</p>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: theme.alpha.black(0.5), zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: wide ? 720 : 560, padding: 40, margin: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16 }}>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 27, margin: 0, fontWeight: 400 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400 }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
